/** Build FP Lab authorise[] from selected page features + roleslists catalog. */

export type FpPageFeature = {
  pageName: string
  url: string
  menu?: string | null
  feature: string[]
  features?: Array<{ label: string; code: string }>
}

/**
 * Create/Edit staff checkbox menus. `false` hides the whole menu and its
 * pages/actions until the agent rail offers them again (e.g. Premium BM).
 */
export const STAFF_FORM_MENU_ENABLED: Readonly<Record<string, boolean>> = {
  'Premium BM Management': false,
}

export function isStaffFormMenuEnabled(menu: string): boolean {
  const key = menu.trim()
  if (Object.prototype.hasOwnProperty.call(STAFF_FORM_MENU_ENABLED, key)) {
    return STAFF_FORM_MENU_ENABLED[key]!
  }
  return true
}

/** Drop disabled menus (and every page/action under them) for staff forms. */
export function filterStaffFormPageFeatures(pageFeatures: FpPageFeature[]): FpPageFeature[] {
  return pageFeatures.filter((page) =>
    isStaffFormMenuEnabled((page.menu || '').trim() || 'Other'),
  )
}

export type FpAuthorisePage = {
  pageName: string
  url: string
  /** Distinguishes same pageName under Book Management vs Premium BM Management. */
  menu?: string | null
  feature: string[]
}

/** Stable checkbox id — menu scopes duplicate page names (e.g. two "Net Exposure"). */
export function featureKey(menu: string, pageName: string, label: string): string {
  return `${menu}\0${pageName}\0${label}`
}

export function selectedFromAuthorise(
  authorise: FpAuthorisePage[] | undefined | null,
  pageFeatures: FpPageFeature[] = [],
  grants: string[] = [],
): string[] {
  if (!Array.isArray(authorise)) return []
  const grantCodes = new Set(grants)
  const out: string[] = []
  for (const page of authorise) {
    const name = String(page?.pageName ?? '').trim()
    if (!name) continue
    const menu = String(page?.menu ?? '').trim()
    for (const label of page?.feature ?? []) {
      if (typeof label !== 'string' || !label.length) continue
      if (menu) {
        // Skip menus/pages omitted from the staff-form catalog (e.g. Premium BM).
        if (
          pageFeatures.length > 0 &&
          !pageFeatures.some(
            (p) =>
              (p.menu || '').trim() === menu &&
              p.pageName === name &&
              (p.features ?? []).some((f) => f.label === label),
          )
        ) {
          continue
        }
        out.push(featureKey(menu, name, label))
        continue
      }
      // Legacy authorise without menu: scope by catalog menu; when pageName collides
      // (Book vs Premium Net Exposure), keep only menus whose code is in allow_access.
      const matches = pageFeatures.filter(
        (p) =>
          p.pageName === name && (p.features ?? []).some((f) => f.label === label),
      )
      if (matches.length === 0) {
        out.push(featureKey('', name, label))
        continue
      }
      if (matches.length === 1) {
        const m = (matches[0]!.menu || '').trim() || 'Other'
        out.push(featureKey(m, name, label))
        continue
      }
      for (const p of matches) {
        const code = (p.features ?? []).find((f) => f.label === label)?.code
        if (!code || !grantCodes.has(code)) continue
        const m = (p.menu || '').trim() || 'Other'
        out.push(featureKey(m, name, label))
      }
    }
  }
  return out
}

/** Build authorise[] from selected menu+page+label keys (not from grant codes). */
export function selectedToAuthorise(
  pageFeatures: FpPageFeature[],
  selectedKeys: string[],
): FpAuthorisePage[] {
  const selected = new Set(selectedKeys)
  const out: FpAuthorisePage[] = []
  for (const page of pageFeatures) {
    const menu = (page.menu || '').trim() || 'Other'
    const defs = page.features ?? []
    const labels = defs
      .filter((f) => selected.has(featureKey(menu, page.pageName, f.label)))
      .map((f) => f.label)
    if (!labels.length) continue
    out.push({
      pageName: page.pageName,
      url: page.url || `/${page.pageName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      menu,
      feature: labels,
    })
  }
  return out
}

/**
 * Legacy hydrate when staff row has allow_access codes but empty authorise.
 * Codes map only to the catalog pages that declare them (menu-scoped).
 */
export function selectedFromGrantCodes(
  pageFeatures: FpPageFeature[],
  grants: string[],
): string[] {
  const codes = new Set(grants)
  const out: string[] = []
  for (const page of pageFeatures) {
    const menu = (page.menu || '').trim() || 'Other'
    for (const f of page.features ?? []) {
      if (codes.has(f.code)) out.push(featureKey(menu, page.pageName, f.label))
    }
  }
  return out
}

/** Roleslists → PermissionMenu tree for existing checkbox UI. */
export function roleslistToMenus(pageFeatures: FpPageFeature[]) {
  const byMenu = new Map<string, Map<string, Array<{ code: string; label: string }>>>()
  for (const page of pageFeatures) {
    const menu = (page.menu || 'Other').trim() || 'Other'
    if (!byMenu.has(menu)) byMenu.set(menu, new Map())
    const roles = byMenu.get(menu)!
    const actions = (page.features ?? []).map((f) => ({ code: f.code, label: f.label }))
    if (actions.length) roles.set(page.pageName, actions)
  }
  return [...byMenu.entries()].map(([menu, roles]) => ({
    menu,
    roles: [...roles.entries()].map(([accessRole, actions]) => ({ accessRole, actions })),
  }))
}
